// Copyright 2025, University of Maryland, All Rights Reserved
import { CASUser, StorageKeys } from "../types";

/**
 * Storage utilities for CAS authentication
 * Uses localStorage for persistence across browser sessions
 */

export const storage = {
  /**
   * Store JWT token in local storage
   */
  setToken(token: string, storageKey: string = StorageKeys.JWT): void {
    try {
      localStorage.setItem(storageKey, token);
    } catch (error) {
      console.error("[CAS Auth] Failed to store token:", error);
    }
  },

  /**
   * Retrieve JWT token from local storage
   */
  getToken(storageKey: string = StorageKeys.JWT): string | null {
    try {
      return localStorage.getItem(storageKey);
    } catch (error) {
      console.error("[CAS Auth] Failed to retrieve token:", error);
      return null;
    }
  },

  /**
   * Remove JWT token from local storage
   */
  removeToken(storageKey: string = StorageKeys.JWT): void {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("[CAS Auth] Failed to remove token:", error);
    }
  },

  /**
   * Store user information in local storage
   */
  setUser(user: CASUser): void {
    try {
      localStorage.setItem(StorageKeys.USER, JSON.stringify(user));
    } catch (error) {
      console.error("[CAS Auth] Failed to store user:", error);
    }
  },

  /**
   * Retrieve user information from local storage
   */
  getUser(): CASUser | null {
    try {
      const userJson = localStorage.getItem(StorageKeys.USER);
      return userJson ? JSON.parse(userJson) : null;
    } catch (error) {
      console.error("[CAS Auth] Failed to retrieve user:", error);
      return null;
    }
  },

  /**
   * Remove user information from local storage
   */
  removeUser(): void {
    try {
      localStorage.removeItem(StorageKeys.USER);
    } catch (error) {
      console.error("[CAS Auth] Failed to remove user:", error);
    }
  },

  /**
   * Store redirect path for post-login navigation
   */
  setRedirectPath(path: string): void {
    try {
      localStorage.setItem(StorageKeys.REDIRECT_PATH, path);
    } catch (error) {
      console.error("[CAS Auth] Failed to store redirect path:", error);
    }
  },

  /**
   * Retrieve and remove redirect path
   */
  getAndClearRedirectPath(): string | null {
    try {
      const path = localStorage.getItem(StorageKeys.REDIRECT_PATH);
      if (path) {
        localStorage.removeItem(StorageKeys.REDIRECT_PATH);
      }
      return path;
    } catch (error) {
      console.error("[CAS Auth] Failed to retrieve redirect path:", error);
      return null;
    }
  },

  /**
   * Get redirect count for infinite loop protection
   */
  getRedirectCount(): number {
    try {
      const count = localStorage.getItem(StorageKeys.REDIRECT_COUNT);
      return count ? parseInt(count, 10) : 0;
    } catch (error) {
      console.error("[CAS Auth] Failed to retrieve redirect count:", error);
      return 0;
    }
  },

  /**
   * Increment redirect count
   */
  incrementRedirectCount(): number {
    try {
      const count = this.getRedirectCount() + 1;
      localStorage.setItem(StorageKeys.REDIRECT_COUNT, count.toString());
      return count;
    } catch (error) {
      console.error("[CAS Auth] Failed to increment redirect count:", error);
      return 1;
    }
  },

  /**
   * Reset redirect count (call after successful authentication)
   */
  resetRedirectCount(): void {
    try {
      localStorage.removeItem(StorageKeys.REDIRECT_COUNT);
    } catch (error) {
      console.error("[CAS Auth] Failed to reset redirect count:", error);
    }
  },

  /**
   * Clear all CAS authentication data
   */
  clearAll(storageKey: string = StorageKeys.JWT): void {
    this.removeToken(storageKey);
    this.removeUser();
    localStorage.removeItem(StorageKeys.REDIRECT_PATH);
    localStorage.removeItem(StorageKeys.REDIRECT_COUNT);
  },

  /**
   * Generic set item in localStorage
   */
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error("[CAS Auth] Failed to store item:", error);
    }
  },

  /**
   * Generic get item from localStorage
   */
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error("[CAS Auth] Failed to retrieve item:", error);
      return null;
    }
  },

  /**
   * Generic remove item from localStorage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("[CAS Auth] Failed to remove item:", error);
    }
  },
};